import { InputGroup, Label } from '@slal/ui/forms';
import { FormProvider, useWatch, yupResolver } from '@slal/ui/react-hook-form/core';
import { Field, RadioFieldGroup, TextField } from '@slal/ui/react-hook-form/fields';
import { ShowWhen, normalizeMonetaryAmount } from '@slal/ui/react-hook-form/utils';
import { Article, P, Text } from '@slal/ui/typography';
import { useNavigate } from 'react-router';
import { MaxWidthContent, Notification, StyledForm } from '~/components';
import { useApplicationContext, useApplicationForm, useNormalizations } from '~/hooks';
import { lumpSumOptionsValidationSchema } from '~/schemas';
import { ApplicationFormKeys, LumpSumOptions, LumpSumOptionsSchema } from '~/types';
import { formatToGBP } from '~/utils';
import { ApplicationButtons } from '../ApplicationButtons';
import { EmergencyTax } from './EmergencyTax';
import { useApplicationTaxFreeCashOptions } from './hooks';
import { StyledFieldSet } from './styles';

export const TaxFreeCashSummary = () => {
    const {
        pensionValues: { maximumTaxFreeCash, total },
    } = useApplicationContext();

    return (
        <Article>
            <P>
                Based on <Text tag="strong">{formatToGBP(total)}</Text> in your pension pot,{' '}
                <Text tag="strong">{formatToGBP(maximumTaxFreeCash)}</Text> of this would be tax-free cash (25%). You
                don't need to take all of your tax-free cash now, you can save some for later.
            </P>
        </Article>
    );
};

export const LumpSumOptionsForm = () => {
    const lumpSumOptions = useApplicationTaxFreeCashOptions();

    const {
        pensionValues: { maximumLumpSum, maximumTaxFreeCash },
        withdrawalOption,
    } = useApplicationContext();

    const { handleSubmit, form } = useApplicationForm<LumpSumOptionsSchema>(
        {
            resolver: yupResolver(lumpSumOptionsValidationSchema(maximumLumpSum)),
        },
        ApplicationFormKeys.LUMP_SUM_OPTIONS
    );

    const navigate = useNavigate();
    const path = withdrawalOption === 'Lump sum' ? 'investment-options' : 'regular-income';
    const onValidCallback = () => navigate(`/application/${path}`);

    const { normalizeMonetaryAmount: normalizeMoney } = useNormalizations({ normalizeMonetaryAmount });

    const specifiedAmount = useWatch({
        control: form.control,
        name: 'lumpSumOptions.specifiedAmount',
    });

    const showEmergencyTaxWarning = specifiedAmount && maximumTaxFreeCash < parseFloat(specifiedAmount);

    return (
        <MaxWidthContent>
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit(onValidCallback)}>
                    <StyledFieldSet aria-label="Lump sum options">
                        <Field label={<TaxFreeCashSummary />} errorFor="lumpSumOptions.option">
                            <RadioFieldGroup name="lumpSumOptions.option" options={lumpSumOptions} stacked />
                        </Field>
                        <ShowWhen fieldName="lumpSumOptions.option" valueEquals={LumpSumOptions.SPECIFY}>
                            <Field
                                label="The first 25% of your pension pot is tax-free. The rest is taxable."
                                labelFor="lumpSumOptions.specifiedAmount"
                                variant="related"
                            >
                                <InputGroup>
                                    <Label>£</Label>
                                    <TextField name="lumpSumOptions.specifiedAmount" setValueAs={normalizeMoney} />
                                </InputGroup>
                            </Field>
                            {showEmergencyTaxWarning && <EmergencyTax />}
                        </ShowWhen>
                    </StyledFieldSet>

                    <Notification>
                        For most people, your total tax-free cash is limited if you have more than £1,073,100 across all
                        your pensions. If you think this affects you, call us.
                    </Notification>

                    <ApplicationButtons backTo="/application/withdrawal-options" />
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};
